import { ChartData, ChartOptions } from "chart.js";
import { useEffect, useState } from "react";
import { ChartType, Period, Testing, ThemeType } from "../types";
import { DataType } from "../types/dataType";
import { generateColor } from "../utils/graphColor";
import { formatDate } from "../utils/wordUtils";

const defaultOptions: ChartOptions<"doughnut"> = {
  responsive: true,
  plugins: {
    legend: {
      position: "top" as const,
    },
  },
  animation: {
    duration: 1500,
    easing: "easeInOutQuart",
  },
  datasets: {
    line: {
      borderColor: "#0ea5e9",
      backgroundColor: "rgba(0,0,0,0)",
      fill: true,
      cubicInterpolationMode: "monotone",
      pointBackgroundColor: "#0ea5e9",
      pointBorderColor: "#0ea5e9",
      borderWidth: 5,
    },
  },
};

const straightChartOpts = {
  scales: {
    xAxes: {
      ticks: {
        color: "rgb(230,230,230)",
      },
    },
    yAxes: {
      ticks: {
        color: "rgb(230,230,230)",
      },
    },
  },
};

export const useOptions = (type: ChartType, theme: ThemeType = "light") => {
  const [option, setOption] = useState<ChartOptions<any>>(defaultOptions);

  useEffect(() => {}, [theme]);
  useEffect(() => {
    if (theme === "dark") {
      switch (type) {
        case "doughnut":
          setOption((o: ChartOptions<any>) => {
            return {
              ...o,
              plugins: {
                legend: {
                  position: "bottom" as const,
                  labels: { color: "rgb(230,230,230)" },
                },
              },
            };
          });
          break;
        case "pie":
          setOption((o: ChartOptions<any>) => {
            return {
              ...o,
              plugins: {
                legend: {
                  position: "bottom" as const,
                  labels: { color: "rgb(230,230,230)" },
                },
              },
            };
          });
          break;
        default:
          setOption((o: ChartOptions<any>) => {
            return {
              ...o,
              plugins: {
                legend: {
                  position: "bottom" as const,
                  labels: { color: "rgb(230,230,230)" },
                },
              },
              ...straightChartOpts,
            };
          });
          break;
      }
    } else {
      setOption((o: ChartOptions<any>) => {
        return {
          ...o,
          scales: {},
          plugins: {
            legend: {
              position: "bottom" as const,
              labels: { color: "rgb(30,30,30)" },
            },
          },
        };
      });
    }
  }, [theme, type]);
  return option;
};

const period = {
  day: Array.from(Array(24).keys()).map((i) => i + 1),
  week: [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ],
  month: Array.from(Array(31).keys()).map((i) => i + 1),
  year: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
};

export const useData = <T extends number | number[]>(
  data: Testing<T>,
  duration?: keyof Period,
  type?: ChartType
): ChartData<any> => {
  const labels = Object.keys(data);

  if (typeof data[labels[0]] === "number") {
    return {
      labels: Object.keys(data),
      datasets: [
        {
          labels: "#",
          data: Object.values(data),
          backgroundColor: generateColor(Object.values(data).length, "border"),
          borderColor: generateColor(Object.values(data).length, "background"),
          borderWidth: 1,
        },
      ],
    };
  }

  const periodData = period[duration || "day"];

  return {
    labels: periodData,
    datasets: Object.keys(data).map((key, index) => ({
      label: key,
      data: data[key],
      backgroundColor: generateColor(Object.keys(data[key]).length, "border")[
        index
      ],
      borderColor: generateColor(Object.keys(data[key]).length, "background")[
        index
      ],
      borderWidth: 1,
    })),
  };
};

export const useNewData = (data: DataType) => {
  return {
    labels: data?.data.map((d) =>
      d[0]?.startsWith("202") ? formatDate(d[0]) : d[0]
    ),
    datasets: data?.head?.slice(1).map((key, index) => ({
      label: key.split(":")[1] || "",
      data: data?.data.map((d) => d[index + 1]),
      backgroundColor: generateColor(data?.head?.slice(1).length, "border")[
        index
      ],
      borderColor: generateColor(data?.head?.slice(1).length, "background")[
        index
      ],
      borderWidth: 1,
    })),
  };
};

export const useRoundData = (data: DataType): ChartData<any> => {
  if (!data.data) {
    return {
      labels: [],
      datasets: [
        {
          label: "No data",
          data: [],
          backgroundColor: generateColor(1, "border"),
          borderColor: generateColor(1, "background"),
          borderWidth: 1,
        },
      ],
    };
  }

  return {
    labels: data?.data.map((d) =>
      d[0]?.startsWith("202") ? formatDate(d[0]) : d[0]
    ),
    datasets: [
      {
        label: "asdakjsdk",
        data: data?.data.map((d) => parseInt(d[1])) || [],
        backgroundColor: generateColor(
          Object.keys(data?.data).length,
          "border"
        ),
        borderColor: generateColor(
          Object.keys(data?.data).length,
          "background"
        ),
        borderWidth: 1,
      },
    ],
  };
};
